import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import {
  type DefaultValues,
  FormProvider,
  type SubmitHandler,
  useForm,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { Box } from '~/components/elements/Box'
import {
  ExecuteCancelDialog,
  type ExecuteCancelDialogProps,
} from '~/components/elements/ExecuteCancelDialog'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useLogEvent } from '~/hooks/useLogEvent'

import { dialogInfoAtom } from '../../atoms'
import { useCloseDialogWithRefresh } from '../../hooks/useCloseDialogWithRefresh'
import { useCustomerAttributeItems } from '../../hooks/useCustomerAttributeItems'
import { useDeleteCustomerAttributeBulkMutation } from '../../hooks/useDeleteCustomerAttributeBulkMutation'

import { AllCheckbox } from './AllCheckbox'
import styles from './DeleteInBulkSelectCustomerAttributesDialog.css'
import { Item } from './Item'
import { type InputValues, resolver } from './utils'

type Step = 'confirm' | 'select'

export const DeleteInBulkSelectCustomerAttributesDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  if (dialogInfo?.dialogType !== 'deleteInBulkSelectCustomerAttributes') {
    throw new Error()
  }

  const resetDialogInfo = useResetAtom(dialogInfoAtom)

  const [step, setStep] = React.useState<Step>('select')
  const [selectedIds, setSelectedIds] = React.useState<string[]>([])
  const { enabledIndices, items } = useCustomerAttributeItems()

  const defaultValues = React.useMemo<DefaultValues<InputValues>>(
    () => ({
      customerAttributes: items.map((item) => ({
        id: item.id,
        checked: !item.isDisabled,
        name: item.name,
      })),
    }),
    [items],
  )

  const methods = useForm<InputValues>({ defaultValues, resolver })
  const {
    formState: { isValid },
    handleSubmit,
    watch,
  } = methods

  const customerAttributes = watch('customerAttributes')
  const checkedCount = customerAttributes.filter(R.prop('checked')).length

  const handleSelectSubmit = React.useCallback<SubmitHandler<InputValues>>(
    ({ customerAttributes }) => {
      const customerAttributeIds = R.pipe(
        customerAttributes,
        R.filter((customerAttribute) => customerAttribute.checked),
        R.map((customerAttribute) => customerAttribute.id),
      )
      setSelectedIds(customerAttributeIds)
      setStep('confirm')
    },
    [],
  )

  const handleConfirmCancel = React.useCallback(() => {
    setStep('select')
  }, [])

  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const logEvent = useLogEvent()
  const deleteCustomerAttributeBulkMutation =
    useDeleteCustomerAttributeBulkMutation({
      onSuccess: (data) => {
        // 選択した中に他から参照されている属性が含まれる場合などdeleteCustomerAttributeBulkはresult: falseを返す。削除できていないので記録しない。
        if (data.deleteCustomerAttributeBulk?.result) {
          logEvent('Delete customer attribute', {
            eventProperties: [
              { key: 'count', value: String(selectedIds.length) },
            ],
          })
        }
        closeDialogWithRefresh()
      },
    })

  const handleConfirmExecute = React.useCallback<
    EH<ExecuteCancelDialogProps, 'onExecute'>
  >(() => {
    deleteCustomerAttributeBulkMutation.mutate(selectedIds)
  }, [selectedIds, deleteCustomerAttributeBulkMutation])

  if (step === 'confirm') {
    return (
      <ExecuteCancelDialog
        executing={deleteCustomerAttributeBulkMutation.isPending}
        mode="delete"
        title={t('delete_checkedcount_customized_segments', {
          checkedCount: selectedIds.length,
        })}
        onCancel={handleConfirmCancel}
        onExecute={handleConfirmExecute}
      >
        <VStack spacing={4}>
          {t('delete_checkedcount_customized_segments_confirmation', {
            checkedCount: selectedIds.length,
          })}

          <Box className={styles.separator} />

          <VStack spacing={2}>
            {customerAttributes.map((attribute) =>
              attribute.checked ? (
                <Typography key={attribute.id} variant="normal">
                  {attribute.name}
                </Typography>
              ) : null,
            )}
          </VStack>
        </VStack>
      </ExecuteCancelDialog>
    )
  }

  return (
    <FormProvider {...methods}>
      <ExecuteCancelDialog
        disabledExecute={!isValid}
        executeButtonText={t('delete_checkedcount_customized_segments', {
          checkedCount,
        })}
        executing={false}
        mode="delete"
        title={t('delete_customized_segments')}
        onCancel={resetDialogInfo}
        onExecute={handleSubmit(handleSelectSubmit)}
      >
        <VStack spacing={4}>
          <Typography variant="normal">
            {t('select_checkedcount_customized_segments_to_delete', {
              checkedCount,
            })}
          </Typography>

          <Box className={styles.grid}>
            <AllCheckbox enabledIndices={enabledIndices} />

            <Typography variant="normal">
              {t('customized_segments_name')}
            </Typography>

            <Box className={styles.separator} />

            {items.map((item) => (
              <Item
                key={item.id}
                disabled={item.isDisabled}
                index={item.index}
              />
            ))}
          </Box>
        </VStack>
      </ExecuteCancelDialog>
    </FormProvider>
  )
}
